import { parseSettingsTabFromSearch } from 'features/settings_modal/constants'

import { TRACKING_EVENT_NAMES } from './constants'

/** `?settings=` section slugs tracked by this module (mirrors `SETTINGS_SECTION` in settings_modal). */
const TRACKED_SETTINGS_SECTION = Object.freeze({
  profile: 'profile',
  calendar: 'calendar',
  branding: 'branding',
  link: 'link',
  workflows: 'workflows',
  ai_notetaker: 'ai_notetaker',
  contacts_settings: 'contacts_settings',
  security: 'security',
})

/** @typedef {'left_nav' | 'profile_dropdown' | 'settings' | 'global_header' | 'help_dropdown'} NavigationLocation */

const NAVIGATION_LOCATION = Object.freeze({
  LEFT_NAV: 'left_nav',
  PROFILE_DROPDOWN: 'profile_dropdown',
  SETTINGS: 'settings',
  GLOBAL_HEADER: 'global_header',
  HELP_DROPDOWN: 'help_dropdown',
})

const PRODUCT_NAME = 'calendly'
const DATA_SOURCE = 'segment'

/**
 * Settings modal sidebar sections from the CUI rebrand tracking plan (May 2026).
 * Keys are `?settings=` section slugs; values are Segment payload fields.
 */
const SETTINGS_SECTION_NAVIGATION = Object.freeze({
  [TRACKED_SETTINGS_SECTION.profile]: {
    navigation_link: 'profile',
    data_analytics_id: 'calendly.left_nav.settings.click_profile',
  },
  [TRACKED_SETTINGS_SECTION.calendar]: {
    navigation_link: 'calendar',
    data_analytics_id: 'calendly.left_nav.settings.click_calendar',
  },
  [TRACKED_SETTINGS_SECTION.branding]: {
    navigation_link: 'branding',
    data_analytics_id: 'calendly.left_nav.settings.click_branding',
  },
  [TRACKED_SETTINGS_SECTION.link]: {
    navigation_link: 'my_link',
    data_analytics_id: 'calendly.left_nav.settings.click_my_link',
  },
  [TRACKED_SETTINGS_SECTION.workflows]: {
    navigation_link: 'notifications',
    data_analytics_id: 'calendly.left_nav.settings.click_notification',
  },
  [TRACKED_SETTINGS_SECTION.ai_notetaker]: {
    navigation_link: 'ai',
    data_analytics_id: 'calendly.left_nav.settings.click_ai',
  },
  [TRACKED_SETTINGS_SECTION.contacts_settings]: {
    navigation_link: 'contacts_settings',
    data_analytics_id: 'calendly.left_nav.setting.click_contacts_settings',
  },
  [TRACKED_SETTINGS_SECTION.security]: {
    navigation_link: 'privacy',
    data_analytics_id: 'calendly.left_nav.settings.click_privacy',
  },
})

/**
 * Settings modal in-section tabs from the CUI rebrand tracking plan.
 * Outer key: parent `?settings=` section slug; inner key: `settings_tab` slug.
 */
const SETTINGS_TAB_NAVIGATION = Object.freeze({
  [TRACKED_SETTINGS_SECTION.profile]: {
    general: {
      navigation_link: 'general',
      data_analytics_id: 'calendly.left_nav.settings.profile.click_general',
    },
    login: {
      navigation_link: 'login',
      data_analytics_id: 'calendly.left_nav.settings.profile.click_login',
    },
  },
  [TRACKED_SETTINGS_SECTION.calendar]: {
    calendar: {
      navigation_link: 'calendar',
      data_analytics_id: 'calendly.left_nav.settings.calendar.click_calendar',
    },
    availability: {
      navigation_link: 'availability',
      data_analytics_id: 'calendly.left_nav.settings.calendar.click_availability',
    },
    advanced: {
      navigation_link: 'advanced',
      data_analytics_id: 'calendly.left_nav.settings.calendar.click_advanced',
    },
  },
  [TRACKED_SETTINGS_SECTION.security]: {
    booking: {
      navigation_link: 'booking',
      data_analytics_id: 'calendly.left_nav.settings.privacy.click_booking',
    },
    blocked_sources: {
      navigation_link: 'blocked_people',
      data_analytics_id: 'calendly.left_nav.settings.privacy.click_blocked_people',
    },
    cookies: {
      navigation_link: 'cookies',
      data_analytics_id: 'calendly.left_nav.settings.privacy.click_cookies',
    },
  },
  [TRACKED_SETTINGS_SECTION.ai_notetaker]: {
    callie: {
      navigation_link: 'callie',
      data_analytics_id: 'calendly.left_nav.settings.ai.click_callie',
    },
    notetaker: {
      navigation_link: 'notetaker',
      data_analytics_id: 'calendly.left_nav.settings.ai.click_notetaker',
    },
  },
})

/** Profile dropdown destinations from the CUI rebrand tracking plan. */
const PROFILE_DROPDOWN_NAVIGATION = Object.freeze({
  profile_dropdown: {
    navigation_link: 'profile_dropdown',
    data_analytics_id: 'calendly.left_nav.click_profile_dropdown',
  },
  profile: {
    navigation_link: 'profile',
    data_analytics_id: 'calendly.left_nav.profile_dropdown.click_profile',
  },
  landing_page: {
    navigation_link: 'landing_page',
    data_analytics_id: 'calendly.left_nav.profile_dropdown.click_landing_page',
  },
  analytics: {
    navigation_link: 'analytics',
    data_analytics_id: 'calendly.left_nav.profile_dropdown.click_analytics',
  },
  integration_and_apps: {
    navigation_link: 'integration_and_apps',
    data_analytics_id: 'calendly.left_nav.profile_dropdown.click_integrations',
  },
  developer: {
    navigation_link: 'developer',
    data_analytics_id: 'calendly.left_nav.profile_dropdown.click_developer',
  },
  visit_calendly: {
    navigation_link: 'visit_calendly',
    data_analytics_id: 'calendly.left_nav.profile_dropdown.click_visit_calendly',
  },
  super_admin: {
    navigation_link: 'super_admin',
    data_analytics_id: 'calendly.left_nav.profile_dropdown.click_super_admin',
  },
  logout: {
    navigation_link: 'logout',
    data_analytics_id: 'calendly.left_nav.profile_dropdown.click_logout',
  },
})

const LEFT_NAV_SETTINGS_NAVIGATION = Object.freeze({
  navigation_link: 'settings',
  data_analytics_id: 'calendly.left_nav.click_settings',
})

/** Global header help dropdown trigger (opens the menu). */
const GLOBAL_HEADER_HELP_DROPDOWN_NAVIGATION = Object.freeze({
  navigation_link: 'help_dropdown',
  data_analytics_id: 'calendly.global_header.click_help_dropdown',
})

/**
 * Help dropdown menu items from the CUI rebrand tracking plan.
 * Keys match {@link HelpCenterMenuItem.key} in `useHelpCenterMenuItems`.
 */
const HELP_DROPDOWN_NAVIGATION = Object.freeze({
  help_center: {
    navigation_link: 'help_center',
    data_analytics_id: 'calendly.global_header.help_dropdown.click_help_center',
  },
  community: {
    navigation_link: 'community',
    data_analytics_id: 'calendly.global_header.help_dropdown.click_community',
  },
  get_started_guide: {
    navigation_link: 'get_started_guide',
    data_analytics_id: 'calendly.global_header.help_dropdown.click_get_started_guide',
  },
  give_feedback: {
    navigation_link: 'give_feedback',
    data_analytics_id: 'calendly.global_header.help_dropdown.click_give_feedback',
  },
})

/** @returns {string | undefined} */
export function navigationAnalyticsIdForLeftNavSettings() {
  return LEFT_NAV_SETTINGS_NAVIGATION.data_analytics_id
}

/** @returns {string | undefined} */
export function navigationAnalyticsIdForGlobalHeaderHelpDropdown() {
  return GLOBAL_HEADER_HELP_DROPDOWN_NAVIGATION.data_analytics_id
}

/**
 * @param {keyof typeof HELP_DROPDOWN_NAVIGATION} linkKey
 * @returns {string | undefined}
 */
export function navigationAnalyticsIdForHelpDropdown(linkKey) {
  return HELP_DROPDOWN_NAVIGATION[linkKey]?.data_analytics_id
}

/**
 * @param {keyof typeof PROFILE_DROPDOWN_NAVIGATION} linkKey
 * @returns {string | undefined}
 */
export function navigationAnalyticsIdForProfileDropdown(linkKey) {
  return PROFILE_DROPDOWN_NAVIGATION[linkKey]?.data_analytics_id
}

/**
 * @param {string} section — `?settings=` section slug
 * @returns {string | undefined}
 */
export function navigationAnalyticsIdForSettingsSection(section) {
  return SETTINGS_SECTION_NAVIGATION[section]?.data_analytics_id
}

/**
 * @param {string} section — parent `?settings=` section slug
 * @param {string} tabSlug — `settings_tab` slug
 * @returns {string | undefined}
 */
export function navigationAnalyticsIdForSettingsTab(section, tabSlug) {
  return SETTINGS_TAB_NAVIGATION[section]?.[tabSlug]?.data_analytics_id
}

/**
 * @param {{
 *   navigationLink: string,
 *   navigationLocation: NavigationLocation,
 *   dataAnalyticsId: string,
 *   sourceUrl: string,
 *   extensionDomain?: string,
 * }} args
 */
function buildNavigationLinkClickedProperties({
  navigationLink,
  navigationLocation,
  dataAnalyticsId,
  sourceUrl,
  extensionDomain,
}) {
  const properties = {
    product_name: PRODUCT_NAME,
    data_source: DATA_SOURCE,
    navigation_link: navigationLink,
    navigation_location: navigationLocation,
    data_analytics_id: dataAnalyticsId,
    source_url: sourceUrl,
  }

  if (extensionDomain != null && extensionDomain !== '') {
    properties.extension_domain = extensionDomain
  }

  return properties
}

/**
 * @param {Function} track — return value of {@link useTrack}
 * @param {{
 *   navigationLink: string,
 *   navigationLocation: NavigationLocation,
 *   dataAnalyticsId: string,
 *   sourceUrl: string,
 *   extensionDomain?: string,
 * }} config
 */
function trackNavigationLinkClicked(track, config) {
  track({
    name: TRACKING_EVENT_NAMES.NAVIGATION_LINK_CLICKED,
    properties: buildNavigationLinkClickedProperties(config),
  })
}

/**
 * @param {Function} track
 * @param {string} sourceUrl
 */
export function trackGlobalHeaderHelpDropdownClick(track, sourceUrl) {
  trackNavigationLinkClicked(track, {
    navigationLink: GLOBAL_HEADER_HELP_DROPDOWN_NAVIGATION.navigation_link,
    navigationLocation: NAVIGATION_LOCATION.GLOBAL_HEADER,
    dataAnalyticsId: navigationAnalyticsIdForGlobalHeaderHelpDropdown(),
    sourceUrl,
  })
}

/**
 * @param {Function} track
 * @param {keyof typeof HELP_DROPDOWN_NAVIGATION} linkKey
 * @param {string} sourceUrl
 * @param {string} [extensionDomain]
 */
export function trackHelpDropdownNavigation(track, linkKey, sourceUrl, extensionDomain) {
  const config = HELP_DROPDOWN_NAVIGATION[linkKey]
  if (config == null) {
    return
  }

  trackNavigationLinkClicked(track, {
    navigationLink: config.navigation_link,
    navigationLocation: NAVIGATION_LOCATION.HELP_DROPDOWN,
    dataAnalyticsId: config.data_analytics_id,
    sourceUrl,
    extensionDomain,
  })
}

/**
 * @param {Function} track
 * @param {string} sourceUrl
 */
export function trackLeftNavSettingsClick(track, sourceUrl) {
  trackNavigationLinkClicked(track, {
    navigationLink: LEFT_NAV_SETTINGS_NAVIGATION.navigation_link,
    navigationLocation: NAVIGATION_LOCATION.LEFT_NAV,
    dataAnalyticsId: navigationAnalyticsIdForLeftNavSettings(),
    sourceUrl,
  })
}

/**
 * @param {Function} track
 * @param {keyof typeof PROFILE_DROPDOWN_NAVIGATION} linkKey
 * @param {string} sourceUrl
 */
export function trackProfileDropdownNavigation(track, linkKey, sourceUrl) {
  const config = PROFILE_DROPDOWN_NAVIGATION[linkKey]
  if (config == null) {
    return
  }

  trackNavigationLinkClicked(track, {
    navigationLink: config.navigation_link,
    navigationLocation: NAVIGATION_LOCATION.PROFILE_DROPDOWN,
    dataAnalyticsId: config.data_analytics_id,
    sourceUrl,
  })
}

/**
 * @param {Function} track
 * @param {string} section — `?settings=` section slug
 * @param {string} sourceUrl
 */
export function trackSettingsSectionNavigation(track, section, sourceUrl) {
  const config = SETTINGS_SECTION_NAVIGATION[section]
  if (config == null) {
    return
  }

  trackNavigationLinkClicked(track, {
    navigationLink: config.navigation_link,
    navigationLocation: NAVIGATION_LOCATION.SETTINGS,
    dataAnalyticsId: config.data_analytics_id,
    sourceUrl,
  })
}

/**
 * @param {Function} track
 * @param {string} section — parent `?settings=` section slug
 * @param {string} tabSlug — `settings_tab` slug
 * @param {string} sourceUrl
 */
export function trackSettingsTabNavigation(track, section, tabSlug, sourceUrl) {
  const sectionTabs = SETTINGS_TAB_NAVIGATION[section]
  const config = sectionTabs?.[tabSlug]
  if (config == null) {
    return
  }

  trackNavigationLinkClicked(track, {
    navigationLink: config.navigation_link,
    navigationLocation: NAVIGATION_LOCATION.SETTINGS,
    dataAnalyticsId: config.data_analytics_id,
    sourceUrl,
  })
}

const PROFILE_TAB_ROUTE_TO_SLUG = Object.freeze({
  '/app/personal/profile/general': 'general',
  '/app/personal/profile/login': 'login',
})

const PRIVACY_TAB_ROUTE_TO_SLUG = Object.freeze({
  '/app/personal/security/booking': 'booking',
  '/app/personal/security/blocked_sources': 'blocked_sources',
  '/app/personal/security/cookies': 'cookies',
})

const PROFILE_BROWSER_BASE = '/app/personal/profile'
const SECURITY_BROWSER_BASE = '/app/personal/security'

/**
 * Maps a memory-route tab path to its `settings_tab` slug for analytics.
 * @param {string} section
 * @param {string | { pathname?: string, search?: string }} routePath
 * @returns {string | null}
 */
export function settingsTabSlugFromRoute(section, routePath) {
  if (typeof routePath === 'object' && routePath != null) {
    const tabSlug = parseSettingsTabFromSearch(routePath.search ?? '')
    if (section === TRACKED_SETTINGS_SECTION.profile && routePath.pathname === PROFILE_BROWSER_BASE) {
      return tabSlug ?? 'general'
    }
    if (section === TRACKED_SETTINGS_SECTION.security && routePath.pathname === SECURITY_BROWSER_BASE) {
      return tabSlug
    }
    return null
  }

  if (section === TRACKED_SETTINGS_SECTION.profile) {
    return PROFILE_TAB_ROUTE_TO_SLUG[routePath] ?? null
  }
  if (section === TRACKED_SETTINGS_SECTION.security) {
    return PRIVACY_TAB_ROUTE_TO_SLUG[routePath] ?? null
  }
  return null
}
